import { Box } from '@chakra-ui/react';
import { Form, useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CloseIcon, PlusIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import usePostTasks from 'Domain/host-task-submission/usePostTasks';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputComponent from 'UiKit/input';
import InputTextAreaComponent from 'UiKit/input-textarea';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonLink from 'UiKit/button/buttonLink';

import { defaultValuesForm } from 'View/host-task-submission/const';

import styles from './styles.module.css';

const MAX_VIDEO_URLS = 3;
const VALID_URL_PREFIXES = ['https://drive.google.com/', 'https://www.tiktok.com/', 'https://vt.tiktok.com/'];

const FormUpsert = () => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValuesForm,
  });

  const { t } = useTranslation('host-content');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'videoUrls',
  });

  const { mutate, isPending } = usePostTasks({ setError });

  const { notes } = watch();

  const onSubmit = (value) => {
    mutate(value);
  };

  const videoUrlValidators = {
    validUrl: (val) => VALID_URL_PREFIXES.some((prefix) => val.startsWith(prefix)) || t('error.invalid_link'),
    duplicate: (val) => {
      const allValues = watch('videoUrls').map((item) => item.value);
      const isDuplicate = allValues.filter((v) => v === val).length > 1;
      return !isDuplicate || t('error.link_already_in_use');
    },
  };

  return (
    <Box className={styles['form__wrapper']}>
      <Box className={styles['head__container']}>
        <TextBold className={styles['head__title--text']}>{t('submit_content.title')}</TextBold>
        <TextRegular className={styles['head__description--text']}>{t('submit_content.description')}</TextRegular>
      </Box>

      <Form control={control} onSubmit={handleSubmit(onSubmit)} className={styles['form__container']}>
        <Box className={styles['form__wrapper-input-url']}>
          <TextBold className={styles['form__label--link']}>
            {t('submit_content.content_link')}
            <span className={styles['form__required--asterisk']}>*</span>
          </TextBold>
          <Iteration
            items={fields}
            render={(field, index) => (
              <Box key={field.id} className={styles['form__box--input-url']}>
                <InputComponent
                  type="url"
                  isRequired={true}
                  pattern="https://*"
                  value={watch(`videoUrls.${index}.value`)}
                  placeholder={'https://'}
                  width={'100%'}
                  className={styles['form__input-url']}
                  error={errors?.videoUrls?.[index]?.value}
                  register={{
                    ...register(`videoUrls.${index}.value`, {
                      required: t('error.cannot_be_empty'),
                      validate: videoUrlValidators,
                    }),
                  }}
                />
                <Condition statement={fields.length > 1}>
                  <CloseIcon size="16px" color="var(--color-neutral60)" onClick={() => remove(index)} className={styles['form__icon--close']} />
                </Condition>
              </Box>
            )}
          />
          <Condition statement={fields.length < MAX_VIDEO_URLS}>
            <ButtonLink type="button" onClick={() => append({ value: '' })} className={styles['form__button--tambah-link']}>
              <PlusIcon size="16px" color="var(--color-primary70)" />{' '}
              <TextSemiBold className={styles['form__text--tambah-link']}>{t('submit_content.add_link')}</TextSemiBold>
            </ButtonLink>
          </Condition>
        </Box>
        <Box>
          <TextBold className={styles['form__label--notes']}>
            {t('submit_content.note')} <span className={styles['form__optional--text']}>{t('submit_content.optional')}</span>
          </TextBold>
          <InputTextAreaComponent placeholder={t('submit_content.note_placeholder')} value={notes} register={{ ...register('notes') }} maxLength={20} />
        </Box>
        <ButtonSolid type="submit" className={styles['form__button--submit']} disabled={isPending}>
          {t('common:submit')}
        </ButtonSolid>
      </Form>
    </Box>
  );
};

export default FormUpsert;
